import { Component, OnDestroy, OnInit, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { parseAsLocalDate } from '../../../../shared/_utils/date-utils';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../../shared/workspace-element-context';
import {
  F1065K2DataEntryService,
  ColumnHeaderData,
  LineData,
  GroupedFormLine,
  FormLineItem,
  FilterParams,
  ClobSettingsObj
} from '../f1065-k2-data-entry.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-f1065-k2-p12s0',
    templateUrl: './f1065-k2-p12s0.component.html',
    styleUrls: ['../f1065-k2.styles.scss', './f1065-k2-p12s0.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F1065K2P12S0Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  private lastContextKey = '';

  // Configuration - unique per component
  private lineDetailsActionCode = 'w5bepp';
  private linkPart = '_P12_S0';
  sectionHeader = 'Section 871(m) Tax Liability of a Qualified Derivatives Dealer (QDD):';

  // Data
  baseURLs: any = {};
  globalService: any = {};
  filterParams: FilterParams = {};
  GENERAL_CONFIG: any = {};
  
  colHeaderData: ColumnHeaderData[] = [];
  lineData: LineData[] = [];
  groupedFormLines: GroupedFormLine[] = [];
  
  // State
  loadingData = true;
  showLoader = true;
  isAdmin = false;
  isSaveClicked = false;
  showErrCol = false;
  isUIErrorSec = '';
  isValidationError = false;
  isInitialized = false;

  // Counters for dropdown loading
  totalDropDownActions = 0;
  loadedActionCounter = 0;

  // Labels
  currentEntityName = '';
  currentPartnerName = '';
  currentCodeGroup = '';
  formSchd = '';

  // Keys
  jcdKey = 0;
  scenario = '';

  // Confirmation modal
  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Save Data',
    message: 'Are you sure to save data?',
    iconClass: 'fa fa-save',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No'
  };

  private isLoading = false;

  constructor(
    private service: F1065K2DataEntryService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwWorkspace.refreshGridData.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    // Explicit initial call — tryInit() has internal dedup (lastContextKey + isLoading),
    // so redundant calls from BehaviorSubject replay are harmless.
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const generalConfig = generalConfigFromGlobal(this.gtwGlobal);
    const baseUrl = generalConfig?.base_url || generalConfig?.api || '';
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};

    this.baseURLs = { ...(this.baseURLs || {}), api: baseUrl };
    this.GENERAL_CONFIG = { ...generalConfig, base_url: baseUrl };
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.scenario = this.filterParams?.scenario || '';
    this.jcdKey = this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || 0;
  }

  private tryInit(): void {
    if (this.isLoading) return;
    
    const hasBaseUrl = this.GENERAL_CONFIG?.base_url;
    const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
    
    if (!hasBaseUrl || !hasFilterParams) {
      return;
    }

    const contextKey = [
      this.GENERAL_CONFIG?.base_url || '',
      this.filterParams?.scenario || '',
      this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '',
      this.filterParams?.p_form || '',
      this.filterParams?.p_schedule || '',
      this.gtwWorkspace.workspaceFactory?.activeScreen?.link || ''
    ].join('|');

    if (contextKey === this.lastContextKey) {
      return;
    }

    this.lastContextKey = contextKey;

    if (this.isInitialized) {
      this.reloadData();
      return;
    }

    console.log('[F1065K2-P12S0] All inputs ready, initializing...');
    this.isInitialized = true;
    this.isLoading = true;
    this.init();
  }

  private reloadData(): void {
    console.log('[F1065K2-P12S0] reloadData() called');
    this.isLoading = true;
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();
    this.colHeaderData = [];
    this.lineData = [];
    this.groupedFormLines = [];
    this.totalDropDownActions = 0;
    this.loadedActionCounter = 0;
    this.init();
  }

  private init(): void {
    console.log('[F1065K2-P12S0] init() - Starting data load');
    this.checkLockStatus();
    this.loadColumnHeaders();
  }

  private checkLockStatus(): void {
    if (!this.GENERAL_CONFIG?.base_url) return;

    this.service.getLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data?.[0]?.LOCK_STATUS === 'Y') {
          this.isAdmin = false;
          this.service.showError(`Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`);
        }
      },
      error: (err) => console.error('[F1065K2-P12S0] Lock status error:', err)
    });
  }

  private setCurrentCodeGroup(): void {
    this.currentCodeGroup = `${this.filterParams.p_form}_${this.filterParams.p_schedule}${this.linkPart}`;
    this.formSchd = `${this.filterParams.p_form}_${this.filterParams.p_schedule}`;
  }

  private setLabelDetails(): void {
    this.currentEntityName = this.filterParams.p_filer_label || '';
    if (this.currentCodeGroup.includes('_K3')) {
      this.currentPartnerName = this.filterParams.p_filer_partner_label || '';
    } else {
      this.currentPartnerName = '';
    }
  }

  private loadColumnHeaders(): void {
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();
    this.setCurrentCodeGroup();
    this.setLabelDetails();

    const taxYear = this.filterParams.tax_year || '';

    this.service.getColumnHeaders(
      this.GENERAL_CONFIG.base_url,
      taxYear,
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        if (!data || data.length === 0) {
          this.service.showWarning('Data Error: No results were returned');
          this.loadingData = true;
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        this.colHeaderData = data;
        let isDropdownAvailable = false;
        this.totalDropDownActions = 0;
        this.loadedActionCounter = 0;

        for (const col of this.colHeaderData) {
          this.setValidationInfo(col, col);
          if (col.DROP_DOWN_ACTION) {
            isDropdownAvailable = true;
            this.totalDropDownActions++;
            this.loadDropdownData(col);
          }
        }

        if (!isDropdownAvailable) {
          this.loadLineDetails();
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[F1065K2-P12S0] Column headers error:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private setValidationInfo(colHeader: any, destObj: any): void {
    if (colHeader.VALIDATION_RULE) {
      const validationRulesArr = colHeader.VALIDATION_RULE.split(';');
      for (const rule of validationRulesArr) {
        if (rule === 'REQ') {
          destObj.IS_MANDATORY = 'Y';
        } else if (rule.startsWith('PREF_REQ')) {
          destObj.IS_PREF_MANDATORY = 'Y';
          if (rule.includes(':') && rule.includes('/')) {
            destObj.PREF_MAN_COLS = rule.split(':')[1].split('/');
            destObj.VALIDATION_MSG = destObj.PREF_MAN_COLS[destObj.PREF_MAN_COLS.length - 1];
            destObj.PREF_MAN_COLS.splice(destObj.PREF_MAN_COLS.length - 1, 1);
          }
        } else if (rule.includes('MAX_LEN:')) {
          destObj.MAX_LENGTH = parseInt(rule.split(':')[1], 10);
        }
      }
    }
  }

  private loadDropdownData(colData: ColumnHeaderData): void {
    const actionDetails = colData.DROP_DOWN_ACTION!.split(':');
    const actionCode = actionDetails[0];
    const codeGroup = actionDetails[1];
    const taxYear = this.filterParams.tax_year || '';

    this.service.getDropdownData(
      this.GENERAL_CONFIG.base_url,
      actionCode,
      codeGroup,
      taxYear,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        colData.actionData = data;
        this.loadedActionCounter++;
        if (this.loadedActionCounter === this.totalDropDownActions) {
          this.loadLineDetails();
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[F1065K2-P12S0] Dropdown error:', err);
        this.loadedActionCounter++;
        if (this.loadedActionCounter === this.totalDropDownActions) {
          this.loadLineDetails();
        }
        this.cdr.detectChanges();
      }
    });
  }

  private loadLineDetails(): void {
    this.showLoader = true;

    const filerKey = this.filterParams.p_filer_key;
    const hasFilerKey = filerKey !== undefined && filerKey !== null && filerKey !== '' && 
                        (typeof filerKey === 'string' ? filerKey.trim().length > 0 : (filerKey as number) >= 0);

    if (!hasFilerKey) {
      this.service.showError('Please select all the filters');
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    if (this.currentCodeGroup.includes('_K3')) {
      const partnerKey = this.filterParams.p_filer_partner_key;
      const hasPartnerKey = partnerKey !== undefined && partnerKey !== null && 
                            (typeof partnerKey === 'string' ? partnerKey.trim().length > 0 : (partnerKey as number) >= 0) &&
                            partnerKey !== -1 && partnerKey !== '-1';
      if (!hasPartnerKey) {
        this.service.showError('Please select all the filters');
        this.isLoading = false;
        this.cdr.detectChanges();
        return;
      }
    }

    this.service.getLineDetails(
      this.GENERAL_CONFIG.base_url,
      this.lineDetailsActionCode,
      this.filterParams.tax_year || '',
      filerKey!,
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams,
      this.filterParams.p_filer_partner_key
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.lineData = data || [];
        this.groupedFormLines = this.processData(this.lineData);
        
        // Apply special post-processing for P12_S0
        this.applyP12S0PostProcessing();
        
        this.showLoader = false;
        this.loadingData = false;
        this.showErrCol = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[F1065K2-P12S0] Line details error:', err);
        this.showLoader = false;
        this.loadingData = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Special post-processing for P12_S0:
   * - Line 5 (SNO == 0): all items disabled (template row for adding "Income type" rows)
   * - Line 6: insert empty placeholder at index 1 (column b is empty for this row)
   * - Last column of each row: disabled (auto-calculated as col[0] × col[1])
   * - Last row columns 0 and 2: disabled (auto-calculated sums) - K2: LINE 17, K3: LINE 6
   */
  private applyP12S0PostProcessing(): void {
    for (const obj of this.groupedFormLines) {
      // Line 5, SNO == 0: disable all items (it's a template row)
      // Use == for LINE_NO (can be number or string from backend)
      if (obj.LINE_NO == '5' && obj.SNO === 0) {
        for (const item of obj.items) {
          item.IS_ENABLED = 'N';
        }
      } else if (obj.LINE_NO == '6') {
        // Insert empty placeholder at index 1 (matching original)
        // This creates the empty cell for column 'a' in line 6
        obj.items.splice(1, 0, {
          SCHD_KEY: '',
          LINE_KEY: '',
          COL_KEY: '',
          COL_DESC: '',
          COL_DESC_INFO: 'a',
          COL_ORDER: 1,
          IS_ENABLED: 'N',
          SNO: 0,
          IS_COL_MULTI: 'N',
          ACTION_Y_N: 'N',
          IS_MANDATORY: 'N',
          OPERATION_TYPE: 'N'
        } as any);
      }
      
      // Last column of each row: disabled (auto-calculated as col[0] × col[1])
      // Mark with IS_CALC_TOTAL flag to distinguish from other disabled columns in pushToDataToSave
      if (obj.items.length > 0) {
        obj.items[obj.items.length - 1].IS_ENABLED = 'N';
        (obj.items[obj.items.length - 1] as any).IS_CALC_TOTAL = true;
      }
    }
    
    // Line 17: disable auto-calculated columns (0 and 2)
    const lastRow = this.groupedFormLines[this.groupedFormLines.length - 1];
    if (lastRow && lastRow.items.length > 2) {
      lastRow.items[0].IS_ENABLED = 'N';  // Column (a) - sum
      lastRow.items[2].IS_ENABLED = 'N';  // Column (c) - sum
    }
    
    // Do NOT call addColumns() here - values come from database on initial load.
    // addColumns() is only called when user changes values (via calcTotalsCol).
  }

  private processData(formLines: LineData[]): GroupedFormLine[] {
    if (formLines && formLines.length > 0) {
      this.isAdmin = true;
    }

    return _.reduce(
      formLines,
      (memo: GroupedFormLine[], property: any) => {
        let bucket = _.find(memo, {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          LINE_NO: property.LINE_NO,
          LINE_DESC: property.LINE_DESC,
          LINE_ORDER: property.LINE_ORDER,
          IS_DISPLAY: 'Y',
          SNO: property.SNO,
          IS_LINE_MULTI: property.IS_LINE_MULTI,
          LINE_SEC: property.LINE_SEC
        });

        if (!bucket) {
          bucket = {
            SCHD_KEY: property.SCHD_KEY,
            LINE_KEY: property.LINE_KEY,
            LINE_NO: property.LINE_NO,
            LINE_DESC: property.LINE_DESC || '',
            LINE_ORDER: property.LINE_ORDER,
            IS_DISPLAY: 'Y',
            SNO: property.SNO,
            IS_LINE_MULTI: property.IS_LINE_MULTI,
            LINE_SEC: property.LINE_SEC,
            items: []
          };
          memo.push(bucket);
        }

        const attribs: any = {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          COL_KEY: property.COL_KEY,
          COL_DESC: property.COL_DESC,
          COL_DESC_INFO: property.COL_DESC_INFO,
          COL_ORDER: property.COL_ORDER,
          IS_ENABLED: 'Y', // Default to Y, post-processing will adjust
          SNO: property.SNO,
          ADJ_KEY: property.ADJ_KEY,
          IS_COL_MULTI: property.IS_COL_MULTI,
          UI_COMP: property.UI_COMP,
          LINE_COMP: property.LINE_COMP,
          ACTION_Y_N: property.ACTION_Y_N,
          IS_MANDATORY: 'N',
          CELL_VALUE: property.CELL_VALUE
        };

        if (attribs.ACTION_Y_N === 'Y') {
          const colObj = this.colHeaderData.find(col => col.COL_KEY === attribs.COL_KEY);
          if (colObj) {
            attribs.actionData = colObj.actionData;
          }
        }

        this.setValidationInfo(property, attribs);

        if (attribs.ADJ_KEY !== undefined && attribs.ADJ_KEY !== null) {
          attribs.OPERATION_TYPE = 'U';
        } else {
          attribs.OPERATION_TYPE = undefined;
        }

        // Set default values only for dynamic rows (SNO > 0), not for template row (SNO === 0)
        // Template row should keep CELL_VALUE as undefined so it's omitted from payload
        if (property.IS_LINE_MULTI === 'Y' && this.isEmptyVal(attribs.CELL_VALUE) && property.SNO > 0) {
          this.setUICompDefaults(attribs);
        }

        if (attribs.UI_COMP === 'DATE' && this.isUICompNotEmpty(attribs)) {
          attribs.CELL_VALUE = parseAsLocalDate(property.CELL_VALUE);
        }

        if (this.isSkipFromSaving(bucket, attribs)) {
          attribs.OPERATION_TYPE = 'N';
        }

        bucket.items.push(attribs);
        return memo;
      },
      []
    );
  }

  private isSkipFromSaving(bucket: GroupedFormLine, attribs: FormLineItem): boolean {
    return attribs.IS_ENABLED === 'N';
  }

  private setUICompDefaults(attrib: any): void {
    if (attrib.UI_COMP === 'NUMERIC') {
      attrib.CELL_VALUE = null;
    } else if (['TEXT', 'DATE', 'CHK_BX', 'DROP_DOWN'].includes(attrib.UI_COMP)) {
      attrib.CELL_VALUE = '';
    }
  }

  private isUICompEmpty(attrib: any): boolean {
    if (attrib.CELL_VALUE === undefined || attrib.CELL_VALUE === null) {
      return true;
    }
    if (attrib.UI_COMP === 'NUMERIC' && attrib.CELL_VALUE === null) {
      return true;
    }
    if (['TEXT', 'DATE', 'CHK_BX', 'DROP_DOWN'].includes(attrib.UI_COMP) && attrib.CELL_VALUE === '') {
      return true;
    }
    return false;
  }

  private isUICompNotEmpty(attrib: any): boolean {
    return !this.isUICompEmpty(attrib);
  }

  private isEmptyVal(val: any): boolean {
    return val === null || val === '' || val === undefined;
  }

  // --- UI Actions ---

  /**
   * Add row - inserts before the last row (line 6)
   * New rows have description "Income type:" and enabled cells (except index 2)
   */
  addRow(selectedRowData: GroupedFormLine): void {
    const tempObj = _.cloneDeep(selectedRowData);
    const multiRow = this.groupedFormLines.filter(
      row => row.LINE_KEY === selectedRowData.LINE_KEY && row.IS_DISPLAY === 'Y'
    );
    const sno = multiRow[multiRow.length - 1].SNO + 1;

    tempObj.LINE_DESC = 'Income type:';
    tempObj.LINE_NO = '';
    tempObj.ERR_MSG = undefined;
    tempObj.SNO = sno;

    // Enable cells for dynamic rows (except index 2)
    for (let i = 0; i < tempObj.items.length; i++) {
      if (i !== 2) {
        tempObj.items[i].IS_ENABLED = 'Y';
      }
      tempObj.items[i].OPERATION_TYPE = 'I';
      tempObj.items[i].ADJ_KEY = undefined;
      tempObj.items[i].CELL_VALUE = '';
      this.setUICompDefaults(tempObj.items[i]);
      tempObj.items[i].SNO = tempObj.SNO;
    }

    // Insert before the last row (line 6)
    this.groupedFormLines.splice(this.groupedFormLines.length - 1, 0, tempObj);
    this.cdr.detectChanges();
  }

  deleteRow(selectedRowData: GroupedFormLine): void {
    const idx = this.groupedFormLines.indexOf(selectedRowData);
    this.groupedFormLines.splice(idx, 1);
    this.addColumns();
    this.cdr.detectChanges();
  }

  /**
   * P12S0 calculation logic:
   * 1. For current row: last column = col[0] × col[1] (Gross amount × Withholding tax rate)
   * 2. Then call addColumns() to recalculate row 17 sums
   * 
   * Called on (ngModelChange) for input fields in rows 1-4, Income types, 12-15
   */
  /**
   * P12S0 calculation logic - called on (blur) event from input fields:
   * 1. For current row: last column = col[0] × col[1] (Gross amount × Withholding tax rate)
   * 2. Then call addColumns() to recalculate row 17 sums
   */
  calcTotalsCol(row: GroupedFormLine, columnValues: any[]): void {
    if (columnValues.length < 2) return;
    
    // Calculate last column = col[0] × col[1]
    // Match original AngularJS behavior exactly:
    // Original: multiply = col[0].CELL_VALUE * col[1].CELL_VALUE;
    // - If col[1] is undefined/null/empty (like LINE 6 placeholder), result is NaN
    //   which serializes to null in JSON
    const val0 = columnValues[0].CELL_VALUE;
    const val1 = columnValues[1].CELL_VALUE;
    
    // Check if col[1] has a valid value (LINE 6 has placeholder with no value)
    const hasValidColB = val1 !== undefined && val1 !== null && val1 !== '';
    
    if (hasValidColB) {
      // Normal calculation
      const colA = val0 ? parseInt(val0, 10) : 0;
      const colB = parseInt(val1, 10);
      columnValues[columnValues.length - 1].CELL_VALUE = colA * colB;
    } else {
      // Missing col[1] (LINE 6 placeholder) → result is null (matches NaN in JSON)
      columnValues[columnValues.length - 1].CELL_VALUE = null;
    }
    
    // Recalculate row 17 sums
    this.addColumns();
    
    this.cdr.detectChanges();
  }
  
  /**
   * Recalculate totals for the LAST row (matches original AngularJS behavior):
   * 
   * Original code: vm.groupedFormLines[vm.groupedFormLines.length - 1]
   * 
   * - K2: last row is LINE 17 "Amount of FTC Claimed After Redetermination"
   * - K3: last row is LINE 6 "Total of line 5 amounts"
   * 
   * Sums column (a) and column (c) from all rows except:
   * - Line 6: excluded to avoid double counting (original behavior)
   * - Last row itself: excluded to prevent self-summation bug
   * - Line 5 (SNO=0): template row (empty)
   */
  private addColumns(): void {
    let col_a = 0;
    let col_b = 0;
    
    // Get the last row - this is where we store the totals
    const lastRow = this.groupedFormLines[this.groupedFormLines.length - 1];
    if (!lastRow || lastRow.items.length < 3) return;
    
    for (const formLines of this.groupedFormLines) {
      // Skip rows that should NOT be summed:
      // Use == (loose comparison) because LINE_NO can be number or string from backend
      const isLine6 = formLines.LINE_NO == '6';            // "Total of line 5 amounts" - avoid double counting
      const isLastRow = formLines === lastRow;             // Don't sum the result row into itself
      const isTemplate = formLines.LINE_NO == '5' && formLines.SNO === 0;  // Template row (empty)
      
      if (!isLine6 && !isLastRow && !isTemplate) {
        const val0 = formLines.items[0]?.CELL_VALUE;
        const val2 = formLines.items[2]?.CELL_VALUE;
        col_a += val0 ? parseInt(val0, 10) : 0;
        col_b += val2 ? parseInt(val2, 10) : 0;
      }
    }
    
    // Store results in the last row
    lastRow.items[0].CELL_VALUE = col_a;
    lastRow.items[2].CELL_VALUE = col_b;
  }

  /**
   * Check if column is the last one (Total column - should be readonly)
   */
  isLastColumn(items: any[], colIdx: number): boolean {
    return items && items.length > 0 && colIdx === items.length - 1;
  }

  /**
   * Handle keypress for NUMERIC inputs - only allow digits and minus at start
   */
  onNumericKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    const charStr = String.fromCharCode(charCode);
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;
    const selectionStart = inputElement.selectionStart || 0;

    // Allow digits 0-9
    if (charCode >= 48 && charCode <= 57) {
      return true;
    }

    // Allow minus sign only at the start (position 0) and if not already present
    if (charStr === '-') {
      if (selectionStart === 0 && !currentValue.includes('-')) {
        return true;
      }
      return false;
    }

    // Block all other characters (including decimal point)
    return false;
  }

  /**
   * Handle paste for NUMERIC inputs - filter to only allowed characters
   */
  onNumericPaste(event: ClipboardEvent, item: any): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    let filtered = pastedText.replace(/[^0-9-]/g, '');
    if (filtered.includes('-')) {
      const hasMinusAtStart = filtered.startsWith('-');
      filtered = filtered.replace(/-/g, '');
      if (hasMinusAtStart) {
        filtered = '-' + filtered;
      }
    }
    item.CELL_VALUE = filtered ? parseInt(filtered, 10) : null;
    this.cdr.detectChanges();
  }

  // --- Save ---

  save(): void {
    this.isSaveClicked = true;
    this.validateForm(this.groupedFormLines);

    if (this.isValidationError) {
      this.service.showError('Please fix all error(s)');
      this.isSaveClicked = false;
      return;
    }

    this.confirmModalConfig = {
      ...this.confirmModalConfig,
      message: `Are you sure to save data for: ${this.currentEntityName}${this.currentPartnerName ? ' / ' + this.currentPartnerName : ''}`
    };
    
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  onSaveConfirm(): void {
    this.showConfirmModal = false;
    const dataToSave: any[] = [];
    this.pushToDataToSave(this.groupedFormLines, dataToSave);

    const returnClobSettingsObj: ClobSettingsObj = {
      jcd_key: this.jcdKey,
      p_form_schd: this.formSchd,
      p_code_group: this.currentCodeGroup
    };

    // Call getLockStatus before save (matching original AngularJS behavior)
    this.service.getLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.filterParams.tax_year || '',
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        // Proceed with save after lock status check
        this.service.saveData(
          this.GENERAL_CONFIG.base_url,
          returnClobSettingsObj,
          dataToSave,
          this.globalService?.globalParams || {},
          this.filterParams,
          this.globalService?.inputs?.tax_year?.values || []
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (result: any) => {
            // Use loose comparison (!=) like original AngularJS: result.data.callSuccess != 1
            if (result?.callSuccess != 1) {
              // Handle errorMessage: "null" string from API
              const errorMsg = (result?.errorMessage && result.errorMessage !== 'null') 
                ? result.errorMessage 
                : 'Save failed';
              this.service.showError(errorMsg);
            } else {
              this.service.showSuccess('Data have been saved/updated');
            }
            this.isSaveClicked = false;
            this.showErrCol = false;
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error('[F1065K2-P12S0] Save error:', err);
            this.service.showError('Save operation failed');
            this.isSaveClicked = false;
            this.cdr.detectChanges();
          }
        });
      },
      error: (err) => {
        console.error('[F1065K2-P12S0] Lock status check error:', err);
        this.isSaveClicked = false;
        this.cdr.detectChanges();
      }
    });
  }

  onSaveCancel(): void {
    this.showConfirmModal = false;
    this.isSaveClicked = false;
    this.cdr.detectChanges();
  }

  reset(): void {
    this.reloadData();
  }

  // --- Validation ---

  private isAnyOtherValueEntered(item: any, itemList: any[]): boolean {
    return itemList.some(x => x !== item && x.IS_ENABLED === 'Y' && this.isUICompNotEmpty(x));
  }

  private validateForm(groupedFormLinesObj: GroupedFormLine[]): void {
    this.isValidationError = false;
    this.isUIErrorSec = '';

    for (const line of groupedFormLinesObj) {
      line.ERR_MSG = undefined;
      let requiredMsgAlreadyAdded = false;

      for (const item of line.items) {
        (item as any).IS_ERROR = 'N';
      }

      for (const item of line.items) {
        if (line.IS_DISPLAY === 'Y' && item.IS_ENABLED !== 'N') {
          if ((item as any).IS_MANDATORY === 'Y' && 
              this.isUICompEmpty(item) && 
              this.isAnyOtherValueEntered(item, line.items)) {
            this.setErrorAttribs(line, item, 'Mandatory field(s) are missing', requiredMsgAlreadyAdded);
            requiredMsgAlreadyAdded = true;
          }
        }
      }
    }
  }

  private setErrorAttribs(line: GroupedFormLine, col: any | null, errMsg: string, requiredMsgAlreadyAdded: boolean): void {
    if (!line.ERR_MSG) {
      line.ERR_MSG = '<ol></ol>';
      this.isUIErrorSec += ',' + (line.LINE_SEC || '');
    }
    if (col) {
      col.IS_ERROR = 'Y';
    }
    this.isValidationError = true;
    if (!requiredMsgAlreadyAdded) {
      line.ERR_MSG = line.ERR_MSG.replace('</ol>', '<li>' + errMsg + '</li></ol>');
    }
    this.showErrCol = true;
  }

  private pushToDataToSave(groupedFormLinesObj: GroupedFormLine[], dataToSave: any[]): void {
    for (const line of groupedFormLinesObj) {
      for (const item of line.items) {
        const returnObj: any = {};
        let cellValue = (item as any).CELL_VALUE;

        // Match original AngularJS behavior exactly:
        // - undefined → for disabled (template rows) don't add key, for enabled send 0
        // - null → for calculated total columns send null, for other disabled send ''
        // - '' (empty string) → for disabled fields keep '', for enabled send 0
        // - has value → convert to number (NaN becomes null in JSON)
        if (cellValue === undefined) {
          // For disabled fields (LINE 5 template): don't add CELL_VALUE key
          // For enabled fields: treat undefined as 0 (matches original)
          if (item.IS_ENABLED !== 'N') {
            returnObj.CELL_VALUE = 0;
          }
        } else if (cellValue === null) {
          // Calculated total columns (last column): send null
          // Other disabled fields (Income types col 2): send empty string
          if ((item as any).IS_CALC_TOTAL) {
            returnObj.CELL_VALUE = null;
          } else if (item.IS_ENABLED === 'N') {
            returnObj.CELL_VALUE = '';
          } else {
            returnObj.CELL_VALUE = null;
          }
        } else if (cellValue === '') {
          // Empty string handling depends on field state
          if (item.IS_ENABLED === 'N') {
            returnObj.CELL_VALUE = '';  // Disabled fields keep empty string
          } else {
            returnObj.CELL_VALUE = 0;   // Enabled fields convert to 0
          }
        } else {
          // Has value - convert to number
          const numValue = Number(cellValue);
          // NaN serializes to null in JSON (matches original behavior)
          returnObj.CELL_VALUE = Number.isNaN(numValue) ? null : numValue;
        }

        returnObj.SCHD_KEY = item.SCHD_KEY;
        returnObj.LINE_NO = line.LINE_NO || '';
        returnObj.LINE_KEY = item.LINE_KEY;
        returnObj.COL_KEY = item.COL_KEY;
        if (item.SNO > 0) {
          returnObj.ADJ_KEY = item.ADJ_KEY !== undefined ? item.ADJ_KEY : null;
        }
        returnObj.SNO = item.SNO;
        returnObj.OPERATION_TYPE = item.OPERATION_TYPE;

        if (item.OPERATION_TYPE === undefined || item.OPERATION_TYPE === null) {
          returnObj.OPERATION_TYPE = 'I';
          item.OPERATION_TYPE = 'U';
        } else if (item.OPERATION_TYPE === 'I') {
          item.OPERATION_TYPE = 'U';
        } else if (item.OPERATION_TYPE === 'N') {
          returnObj.OPERATION_TYPE = null;
        }

        if (returnObj.OPERATION_TYPE !== null) {
          dataToSave.push(returnObj);
        }
      }
    }
  }

  // --- Helper for template ---

  /**
   * Check if the input should be hidden (for line 6, column index 1)
   */
  shouldHideInput(row: GroupedFormLine, colIndex: number): boolean {
    // Use == for LINE_NO (can be number or string from backend)
    return row.LINE_NO == '6' && colIndex === 1;
  }

  trackByRow(index: number, row: GroupedFormLine): string {
    return `${row.LINE_KEY}_${row.SNO}_${row.IS_DISPLAY}_${index}`;
  }

  trackByCol(index: number, col: FormLineItem): string {
    return `${col.COL_KEY}_${col.SNO}_${index}`;
  }
}
